import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, ArrowRight, Check, Building2, Users, Globe, Mail, Phone, Lock, Briefcase, Building, PenTool, Zap, Target, Brain, BarChart as ChartBar, CheckCircle } from 'lucide-react';

// Declare global recaptcha type if not already declared
declare global {
  interface Window {
    grecaptcha: any;
    onRecaptchaLoad: () => void;
  }
}

const GetStarted = () => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<{
    companyName: string;
    industry: string;
    otherIndustry: string;
    size: string;
    requirements: string[];
    otherRequirements: string;
    consultingNeeds: string[];
    email: string;
    phone: string;
  }>({
    companyName: '',
    industry: '',
    otherIndustry: '',
    size: '',
    requirements: [],
    otherRequirements: '',
    consultingNeeds: [],
    email: '',
    phone: ''
  });
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [showRecaptchaError, setShowRecaptchaError] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Load reCAPTCHA script when form reaches step 3
  useEffect(() => {
    if (step === 3) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
      script.async = true;
      script.defer = true;
      
      window.onRecaptchaLoad = () => {
        setRecaptchaLoaded(true);
      };
      
      document.body.appendChild(script);
      
      return () => {
        document.body.removeChild(script);
        (window as any).onRecaptchaLoad = undefined;
      };
    }
  }, [step]);
  
  // Initialize reCAPTCHA when it's loaded and form is at step 3
  useEffect(() => {
    if (recaptchaLoaded && window.grecaptcha && step === 3) {
      try {
        window.grecaptcha.render('getstarted-recaptcha-container', {
          sitekey: '6LeWfxorAAAAAHuXucPcVo0Rd0nCrIkXjezFDR8e'
        });
      } catch (error) {
        console.error('Error rendering reCAPTCHA:', error);
      }
    }
  }, [recaptchaLoaded, step]);

  const benefits = [
    {
      icon: Shield,
      title: "Comprehensive Protection",
      description: "End-to-end security solutions tailored to your organization's unique needs and challenges."
    },
    {
      icon: Target,
      title: "Strategic Approach",
      description: "Data-driven security strategies aligned with your business objectives and risk profile."
    },
    {
      icon: Brain,
      title: "Expert Guidance",
      description: "Access to certified security professionals with deep industry expertise and experience."
    },
    {
      icon: ChartBar,
      title: "Measurable Results",
      description: "Clear metrics and reporting to demonstrate security improvements and ROI."
    }
  ];

  const solutions = [
    {
      title: "Managed Security Services",
      features: [
        "24/7 Security Monitoring",
        "Threat Detection & Response",
        "Security Infrastructure Management",
        "Continuous Protection"
      ]
    },
    {
      title: "Security Consulting",
      features: [
        "Risk Assessment",
        "Security Strategy Development",
        "Compliance Management",
        "Security Architecture Design"
      ]
    },
    {
      title: "Specialized Solutions",
      features: [
        "Cloud Security",
        "Data Protection",
        "Identity Management",
        "Incident Response"
      ]
    }
  ];

  const industries = [
    { icon: Building2, name: 'Financial Services' },
    { icon: Users, name: 'Healthcare' },
    { icon: Globe, name: 'Technology' },
    { icon: Shield, name: 'Government' },
    { icon: Building, name: 'Manufacturing' },
    { icon: PenTool, name: 'Other' }
  ];

  const companySizes = [
    '1-50 employees',
    '51-200 employees',
    '201-500 employees',
    '500+ employees'
  ];

  const securityRequirements = [
    'Network Security',
    'Cloud Security',
    'Data Protection',
    'Compliance & Risk Management',
    'Security Operations Center',
    'Incident Response',
    'Employee Training',
    'Other'
  ];

  const consultingServices = [
    'Security Strategy Development',
    'Risk Assessment',
    'Compliance Consulting',
    'Security Architecture Review',
    'Digital Transformation',
    'Process Optimization',
    'Technology Implementation',
    'Security Training & Awareness'
  ];

  const startConsultation = () => {
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const handleNext = () => {
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const handleBack = () => {
    setStep(step - 1);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset error state
    setShowRecaptchaError(false);
    
    // Only verify reCAPTCHA at the final step
    if (step === 3) {
      // Verify reCAPTCHA
      if (window.grecaptcha) {
        const recaptchaResponse = window.grecaptcha.getResponse();
        if (!recaptchaResponse) {
          setShowRecaptchaError(true);
          return;
        }
        
        // If reCAPTCHA verification passed, submit the form
        const form = e.target as HTMLFormElement;
        
        // Create a FormData object to get the form data
        const formDataObj = new FormData(form);
        
        // Submit the form using fetch
        fetch(form.action, {
          method: form.method,
          body: formDataObj,
          headers: {
            Accept: 'application/json'
          }
        })
          .then(response => {
            if (response.ok) {
              // Show thank you message
              setFormSubmitted(true);
              // Reset form data if needed
              // setFormData({...}); // If you want to reset the form
            } else {
              console.error('Form submission failed');
              alert('There was a problem submitting your form. Please try again.');
            }
          })
          .catch(error => {
            console.error('Form submission error:', error);
            alert('There was a problem submitting your form. Please try again.');
          });
      } else {
        console.error('reCAPTCHA not loaded');
        setShowRecaptchaError(true);
      }
    } else {
      // Handle intermediate steps
      console.log(formData);
    }
  };

  if (step === 0) {
    return (
      <div className="min-h-screen hero-gradient pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block mb-6"
            >
              <Shield className="h-16 w-16 text-primary animate-glow" />
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              <span className="text-gradient">Transform Your Security Posture</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg text-muted max-w-3xl mx-auto mb-8"
            >
              Partner with AstronShield to protect your organization with advanced cybersecurity solutions and expert consulting services
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="btn-primary"
              onClick={startConsultation}
            >
              <span className="flex items-center">
                Start Your Security Journey
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </motion.button>
          </div>

          {/* Benefits Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20"
          >
            {benefits.map((benefit, index) => (
              <div key={index} className="glass-panel rounded-xl p-6 hover-card">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg mb-6">
                  <benefit.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-muted">{benefit.description}</p>
              </div>
            ))}
          </motion.div>

          {/* Solutions Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Comprehensive Security Solutions</h2>
              <p className="text-lg text-muted">Tailored security services to protect your organization</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {solutions.map((solution, index) => (
                <div key={index} className="glass-panel rounded-xl p-8 hover-card">
                  <h3 className="text-2xl font-semibold mb-6">{solution.title}</h3>
                  <ul className="space-y-4">
                    {solution.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <Check className="h-5 w-5 text-primary mr-3" />
                        <span className="text-muted">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-center"
          >
            <div className="glass-panel rounded-2xl p-8 md:p-12">
              <Zap className="h-12 w-12 text-primary mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-6">Ready to Secure Your Future?</h2>
              <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
                Schedule a consultation with our security experts to discuss your organization's needs and discover how we can help protect your digital assets
              </p>
              <button 
                className="btn-primary"
                onClick={startConsultation}
              >
                <span className="flex items-center justify-center">
                  Request Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </span>
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Shield className="h-16 w-16 text-primary mx-auto" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            <span className="text-gradient">Get Started with AstronShield</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted"
          >
            Let's understand your security needs and create a tailored solution
          </motion.p>
        </div>

        {/* Progress Steps - only show if not submitted */}
        {!formSubmitted && (
          <div className="flex justify-between items-center mb-12 relative">
            <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-primary/20 -z-10" />
            {[1, 2, 3].map((number) => (
              <motion.div
                key={number}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: number * 0.1 }}
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  step >= number ? 'bg-primary text-white' : 'bg-primary/10 text-primary'
                }`}
              >
                {step > number ? <Check className="w-5 h-5" /> : number}
              </motion.div>
            ))}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-panel rounded-2xl p-8"
        >
          {formSubmitted ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center p-8 glass-panel rounded-xl border-2 border-primary"
            >
              <CheckCircle className="h-16 w-16 text-primary mx-auto mb-6" />
              <h3 className="text-2xl font-bold mb-4">Thank You for Getting Started!</h3>
              <p className="text-lg text-muted mb-6">
                We've received your information and are excited to begin our security partnership. Our team will review your requirements and reach out to you within 24 hours to discuss the next steps in securing your organization.
              </p>
              <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => {
                    setFormSubmitted(false);
                    setStep(0);
                  }}
                  className="btn-primary px-8"
                >
                  <span className="flex items-center justify-center">
                    Start New Request
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </span>
                </button>
                <button
                  onClick={() => window.location.href = '/'}
                  className="px-8 py-2 rounded-full border border-primary text-primary hover:bg-primary/10 transition-colors"
                >
                  Return to Home
                </button>
              </div>
            </motion.div>
          ) : (
            <form action="https://formspree.io/f/xovebokk" method="POST" onSubmit={handleSubmit}>
              <input type="hidden" name="form-name" value="getting-started" />
              <input type="hidden" name="_subject" value={`New Getting Started Request - Industry: ${formData.industry}`} />
              
              {/* Form data summary for better email notification */}
              <input type="hidden" name="form_summary" value={`
Industry: ${formData.industry}
Company Size: ${formData.size}
Security Requirements: ${formData.requirements.join(', ')}
Consulting Services: ${formData.consultingNeeds.join(', ')}
              `.trim()} />
              
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Company Name</label>
                    <input
                      type="text"
                      name="companyName"
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                      value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      placeholder="Enter your company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-4">Industry</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {industries.map((industry) => (
                        <button
                          key={industry.name}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, industry: industry.name });
                            
                            const subjectField = document.querySelector('input[name="_subject"]') as HTMLInputElement;
                            if (subjectField) {
                              subjectField.value = `New Getting Started Request - Industry: ${industry.name}`;
                            }
                          }}
                          className={`p-4 rounded-xl border ${
                            formData.industry === industry.name
                              ? 'border-primary bg-primary/10'
                              : 'border-white/10 hover:border-primary/50'
                          } transition-colors`}
                        >
                          <industry.icon className="h-8 w-8 mb-2 mx-auto text-primary" />
                          <span className="block text-sm">{industry.name}</span>
                        </button>
                      ))}
                    </div>
                    <input type="hidden" name="industry" value={formData.industry} />
                    {formData.industry === 'Other' && (
                      <input
                        type="text"
                        name="otherIndustry"
                        className="mt-4 w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                        value={formData.otherIndustry}
                        onChange={(e) => setFormData({ ...formData, otherIndustry: e.target.value })}
                        placeholder="Please specify your industry"
                      />
                    )}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-4">Company Size</label>
                    <div className="grid grid-cols-2 gap-4">
                      {companySizes.map((size) => (
                        <button
                          key={size}
                          type="button"
                          onClick={() => setFormData({ ...formData, size })}
                          className={`p-4 rounded-xl border ${
                            formData.size === size
                              ? 'border-primary bg-primary/10'
                              : 'border-white/10 hover:border-primary/50'
                          } transition-colors`}
                        >
                          <Users className="h-6 w-6 mb-2 mx-auto text-primary" />
                          <span className="block text-sm">{size}</span>
                        </button>
                      ))}
                    </div>
                    <input type="hidden" name="companySize" value={formData.size} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-4">Security Requirements</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {securityRequirements.map((requirement) => (
                        <button
                          key={requirement}
                          type="button"
                          onClick={() => {
                            const requirements = formData.requirements.includes(requirement)
                              ? formData.requirements.filter((r) => r !== requirement)
                              : [...formData.requirements, requirement];
                            setFormData({ ...formData, requirements });
                          }}
                          className={`p-4 rounded-xl border ${
                            formData.requirements.includes(requirement)
                              ? 'border-primary bg-primary/10'
                              : 'border-white/10 hover:border-primary/50'
                          } transition-colors text-left`}
                        >
                          <Lock className="h-5 w-5 mb-2 text-primary" />
                          <span className="block text-sm">{requirement}</span>
                        </button>
                      ))}
                    </div>
                    <input type="hidden" name="securityRequirements" value={formData.requirements.join(', ')} />
                    {formData.requirements.includes('Other') && (
                      <textarea
                        name="otherRequirements"
                        className="mt-4 w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                        value={formData.otherRequirements}
                        onChange={(e) => setFormData({ ...formData, otherRequirements: e.target.value })}
                        placeholder="Please specify your security requirements"
                        rows={3}
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-4">Consulting Services Needed</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {consultingServices.map((service) => (
                        <button
                          key={service}
                          type="button"
                          onClick={() => {
                            const consultingNeeds = formData.consultingNeeds.includes(service)
                              ? formData.consultingNeeds.filter((s) => s !== service)
                              : [...formData.consultingNeeds, service];
                            setFormData({ ...formData, consultingNeeds });
                          }}
                          className={`p-4 rounded-xl border ${
                            formData.consultingNeeds.includes(service)
                              ? 'border-primary bg-primary/10'
                              : 'border-white/10 hover:border-primary/50'
                          } transition-colors text-left`}
                        >
                          <Briefcase className="h-5 w-5 mb-2 text-primary" />
                          <span className="block text-sm">{service}</span>
                        </button>
                      ))}
                    </div>
                    <input type="hidden" name="consultingServices" value={formData.consultingNeeds.join(', ')} />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted" />
                      <input
                        type="email"
                        name="email"
                        className="w-full pl-12 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Phone Number</label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted" />
                      <input
                        type="tel"
                        name="phone"
                        className="w-full pl-12 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>
                  
                  {/* reCAPTCHA */}
                  <div className="flex flex-col items-center">
                    <div id="getstarted-recaptcha-container"></div>
                    {showRecaptchaError && (
                      <p className="text-red-500 mt-2">Please complete the reCAPTCHA before submitting.</p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-between mt-8">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="px-6 py-2 rounded-full border border-white/10 hover:bg-white/5 transition-colors"
                  >
                    Back
                  </button>
                )}
                {step < 3 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="ml-auto btn-primary"
                  >
                    <span className="flex items-center">
                      Next Step
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </span>
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="ml-auto btn-primary"
                  >
                    <span className="flex items-center">
                      Get Started
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </span>
                  </button>
                )}
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default GetStarted;